export const DEFAULT_MIC_CONSTRAINTS: MediaStreamConstraints = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
  video: false,
};

export const getConstraintsWithDevice = (deviceId?: string): MediaStreamConstraints => {
  if (!deviceId || deviceId === 'default') {
    return DEFAULT_MIC_CONSTRAINTS;
  }
  
  return {
    audio: {
      deviceId: { exact: deviceId },
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
    video: false,
  };
};

export type LocalMicControllerOptions = {
  constraints?: MediaStreamConstraints;
};

export class LocalMicController {
  private localStream: MediaStream | null = null;
  private isMuted = false;
  private readonly constraints: MediaStreamConstraints;

  constructor({ constraints = DEFAULT_MIC_CONSTRAINTS }: LocalMicControllerOptions = {}) {
    this.constraints = constraints;
  }

  public async requestPermission(overrides?: MediaStreamConstraints): Promise<MediaStream> {
    if (this.localStream) return this.localStream;

    const constraints = overrides ?? this.constraints;
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia(constraints);
      return this.localStream;
    } catch (err) {
      throw new Error(
        'Failed to get microphone permission: ' + (err instanceof Error ? err.message : String(err)),
      );
    }
  }

  public getLocalStream(): MediaStream {
    if (!this.localStream) {
      throw new Error('No local media stream available. Call requestPermission first.');
    }
    return this.localStream;
  }

  public mute(): void {
    if (!this.localStream) return;
    const [track] = this.localStream.getAudioTracks();
    if (track && track.enabled) {
      track.enabled = false;
      this.isMuted = true;
    }
  }

  public unmute(): void {
    if (!this.localStream) return;
    const [track] = this.localStream.getAudioTracks();
    if (track && !track.enabled) {
      track.enabled = true;
      this.isMuted = false;
    }
  }

  public toggleMute(): boolean {
    if (!this.localStream) return this.isMuted;
    const audioTracks = this.localStream.getAudioTracks();
    audioTracks.forEach((track) => {
      track.enabled = !track.enabled;
    });
    if (audioTracks.length) {
      this.isMuted = !audioTracks[0].enabled;
    }
    return this.isMuted;
  }

  public getMuteState(): boolean {
    return this.isMuted;
  }

  public cleanup(): void {
    this.localStream?.getTracks().forEach((track) => track.stop());
    this.localStream = null;
    this.isMuted = false;
  }
} 